
import React, { useState, useMemo } from 'react';
import { DailyHistory } from '../types';
import { getTodayDateString } from '../utils/date';

interface CalendarViewProps {
  history: { [date: string]: DailyHistory };
  tokenIcon: string;
}

const CalendarView: React.FC<CalendarViewProps> = ({ history, tokenIcon }) => {
  const [date, setDate] = useState(new Date());

  const { month, year, daysInMonth, firstDayOfMonth } = useMemo(() => {
    const month = date.getMonth();
    const year = date.getFullYear();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return { month, year, daysInMonth, firstDayOfMonth };
  }, [date]);

  const monthName = date.toLocaleString('en-GB', { month: 'long', timeZone: 'Europe/London' });
  const todayString = getTodayDateString();

  const renderDays = () => {
    const days = [];
    // Add blank days for the start of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`blank-${i}`} className="p-1"></div>);
    }
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(year, month, day);
      const dateString = dayDate.toISOString().split('T')[0];
      const dayHistory = history[dateString];
      const isToday = dateString === todayString;
      
      days.push(
        <div key={day} className={`group relative p-1 border border-slate-300/50 dark:border-slate-700/50 rounded-md flex flex-col items-center justify-start h-14 ${isToday ? 'bg-sky-100 dark:bg-sky-900/50' : ''}`}>
          <span className={`text-xs ${isToday ? 'text-sky-600 dark:text-sky-300 font-bold' : 'text-slate-600 dark:text-slate-400'}`}>{day}</span>
          {dayHistory && (
             <div className="mt-1 flex flex-wrap gap-1 justify-center items-center">
              {dayHistory.tokens > 0 && Array.from({length: dayHistory.tokens}).map((_, i) => (
                <span key={i} className="text-xs leading-none">{tokenIcon}</span>
              ))}
              {dayHistory.questsCompleted.length > 0 && 
                <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center text-white text-[8px] font-bold">
                  {dayHistory.questsCompleted.length}
                </div>
              }
            </div>
          )}
          {dayHistory && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg shadow-xl z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <p className="font-bold text-slate-800 dark:text-slate-200">{dayDate.toLocaleDateString('en-GB', {weekday: 'long', month: 'long', day: 'numeric', timeZone: 'Europe/London'})}</p>
              <div className="mt-2 text-sm space-y-2 text-slate-700 dark:text-slate-300">
                <p>Unspent Tokens: <span className="font-bold">{dayHistory.tokens}</span></p>
                {dayHistory.questsCompleted.length > 0 && (
                   <div>
                     <p>Quests Completed:</p>
                     <ul className="list-disc list-inside text-slate-600 dark:text-slate-400">
                      {dayHistory.questsCompleted.map(q => <li key={q.id}>{q.title}</li>)}
                     </ul>
                   </div>
                )}
              </div>
              <div className="absolute bottom-[-5px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white dark:bg-slate-900 border-b border-r border-slate-300 dark:border-slate-600 rotate-45"></div>
            </div>
          )}
        </div>
      );
    }
    return days;
  };
  
  const changeMonth = (amount: number) => {
      setDate(d => new Date(d.getFullYear(), d.getMonth() + amount, 1));
  }

  return (
    <div className="bg-slate-200/50 dark:bg-slate-800/50 rounded-xl p-6 border border-slate-300 dark:border-slate-700 flex-shrink-0">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => changeMonth(-1)} className="px-3 py-1 bg-slate-300 dark:bg-slate-700 rounded-md hover:bg-slate-400 dark:hover:bg-slate-600">&lt;</button>
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{monthName} {year}</h2>
        <button onClick={() => changeMonth(1)} className="px-3 py-1 bg-slate-300 dark:bg-slate-700 rounded-md hover:bg-slate-400 dark:hover:bg-slate-600">&gt;</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => <div key={d} className="text-slate-500 font-bold text-xs">{d}</div>)}
        {renderDays()}
      </div>
    </div>
  );
};

export default CalendarView;
